import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Typography,
    Box,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

/**
 * BillingRequiredDialog — Shown when a user tries to create a case without QBO billing setup.
 * 
 * Displays an error message explaining that billing must be set up before creating cases,
 * with a button to navigate to billing settings.
 */

function BillingRequiredDialog({ open, onClose }) {
    const handleNavigateToBilling = () => {
        // Close the dialog and navigate to billing settings
        if (onClose) {
            onClose();
        }
        
        // Navigate to billing settings page
        window.location.href = '/billing_settings';
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: '16px',
                },
            }}
        >
            <DialogTitle 
                sx={{ 
                    pb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontSize: '1.25rem',
                }}
            >
                <WarningIcon color="warning" sx={{ fontSize: 32 }} />
                Billing Setup Required
            </DialogTitle>
            
            <DialogContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, p: 2, bgcolor: 'warning.lighter', borderRadius: 1 }}>
                    <AccountBalanceIcon color="primary" sx={{ fontSize: 48, mr: 2 }} />
                    <Box>
                        <Typography variant="subtitle1" component="div" fontWeight="600">
                            QuickBooks Online Not Set Up
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            You need to configure billing before creating cases
                        </Typography>
                    </Box>
                </Box>

                <DialogContentText sx={{ mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                    To create and manage cases in Livisibly Portal, you must first set up your 
                    QuickBooks Online (QBO) billing account. This is required for all case operations 
                    and ensures proper billing management.
                </DialogContentText>

                <Box sx={{ 
                    p: 2, 
                    bgcolor: 'grey.50', 
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                }}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>What you'll need to do:</strong>
                    </Typography>
                    <ul style={{ margin: '8px 0 0 20px', padding: 0, fontSize: '0.9rem', color: '#666' }}>
                        <li style={{ marginBottom: '4px' }}>Create a billing account (individual or organization)</li>
                        <li style={{ marginBottom: '4px' }}>Add a payment method (credit card or bank account)</li>
                        <li>Select your billing plan</li>
                    </ul>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button 
                    onClick={onClose} 
                    variant="outlined"
                    sx={{ color: 'text.secondary' }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleNavigateToBilling} 
                    variant="contained"
                    color="primary"
                    startIcon={<AccountBalanceIcon />}
                    autoFocus
                >
                    Set Up Billing
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default BillingRequiredDialog;
